import React, { useCallback, useEffect, useRef, useState, KeyboardEvent } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminAPIResource } from "constants/adminApi";
import { ROUTES_VARIANT } from "constants/routesVariant";
import adminApiKeyAxiosInstance from "utils/adminApiKeyAxiosInstance";
import { getUrl } from "utils/getURL";
import AdminPageHeader from "components/pages/admin/components/AdminPageHeader";
import NameMatchResultView from "./NameMatchResultView";
import { NameMatchEvaluateResponse } from "./types";

export default function NameMatchTester() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialPan = searchParams.get("panName")?.trim() || "";
  const initialBank = searchParams.get("bankName")?.trim() || "";
  const autoRanRef = useRef(false);

  const [panName, setPanName] = useState(initialPan);
  const [bankName, setBankName] = useState(initialBank);
  const [result, setResult] = useState<NameMatchEvaluateResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runTest = useCallback(async (panOverride?: string, bankOverride?: string) => {
    const pan = (panOverride ?? panName).trim();
    const bank = (bankOverride ?? bankName).trim();
    if (!pan || !bank) {
      setError("Enter both PAN name and Bank name.");
      setResult(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await adminApiKeyAxiosInstance.post<NameMatchEvaluateResponse>(
        getUrl(AdminAPIResource.nameMatchEvaluate),
        { panName: pan, bankName: bank }
      );
      setResult(response.data);
    } catch (err: unknown) {
      setResult(null);
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message
        || (err as { message?: string })?.message
        || "Failed to evaluate name match";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [panName, bankName]);

  useEffect(() => {
    if (autoRanRef.current) return;
    if (!initialPan || !initialBank) return;
    autoRanRef.current = true;
    void runTest(initialPan, initialBank);
  }, [initialPan, initialBank, runTest]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !loading) void runTest();
  };

  const clearAll = () => {
    setPanName("");
    setBankName("");
    setResult(null);
    setError(null);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f4f8" }}>
      <AdminPageHeader
        title="Name match tester"
        subtitle="Compare PAN and bank account names using the production matching engine"
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/${ROUTES_VARIANT.VERIFIED_KYC_USERS}`)}
          >
            Back to customers
          </Button>
        }
      />

      <Box sx={{ maxWidth: 720, mx: "auto", px: 3, py: 3 }}>
        <Paper elevation={0} sx={{ border: "1px solid #e3e8ef", borderRadius: 2, p: 2.5, mb: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="PAN card name"
              value={panName}
              onChange={(e) => setPanName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. RAJESH KUMAR SHARMA"
              fullWidth
              size="small"
              disabled={loading}
              inputProps={{ style: { fontWeight: 600 } }}
            />
            <TextField
              label="Bank account name"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. RAJESH K SHARMA"
              fullWidth
              size="small"
              disabled={loading}
              inputProps={{ style: { fontWeight: 600 } }}
            />
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => void runTest()}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: 1.5,
                  px: 3,
                  bgcolor: "#007CBF",
                  "&:hover": { bgcolor: "#006aa3" },
                }}
              >
                {loading ? "Checking…" : "Check match"}
              </Button>
              <Button
                variant="outlined"
                onClick={clearAll}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  color: "#6c757d",
                  borderColor: "#ced4da",
                }}
              >
                Clear
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {!result && !loading && !error && (
          <Paper
            elevation={0}
            sx={{
              border: "1px dashed #ced4da",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              color: "#6c757d",
              bgcolor: "#fff",
            }}
          >
            <Typography variant="body2">
              Enter both names and click <strong>Check match</strong>.
            </Typography>
            <Typography variant="caption" display="block" mt={0.75} color="text.secondary">
              Useful when reviewing on-hold / name-mismatch cases.
            </Typography>
          </Paper>
        )}

        {loading && !result && (
          <Paper
            elevation={0}
            sx={{
              border: "1px solid #e3e8ef",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              bgcolor: "#fff",
            }}
          >
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" mt={1.5}>
              Checking match…
            </Typography>
          </Paper>
        )}

        {result && (
          <NameMatchResultView
            result={result}
            prioritizeFailures={result.verdict === "REJECT"}
          />
        )}
      </Box>
    </Box>
  );
}
